// frontend/src/lib/dramaDownload/downloader.ts
import type { DownloadStats, DownloadTask, ProgressEvent } from './types';
import { isRecorded, loadManifest, manifestKey, manifestSource, saveManifest } from './manifest';
import { fileExists, getParentDirAndName, writeStream } from './fsAccess';

const DEFAULT_CONCURRENCY = 5;
const ALLOWED_HOST_SUFFIX = '.volces.com';

export function isAllowedVideoUrl(url: string): boolean {
  try {
    return new URL(url).hostname.toLowerCase().endsWith(ALLOWED_HOST_SUFFIX);
  } catch {
    return false;
  }
}

export interface RunOptions {
  root: FileSystemDirectoryHandle;
  tasks: DownloadTask[];
  signal: AbortSignal;
  onProgress: (e: ProgressEvent) => void;
  concurrency?: number;
  fetchImpl?: typeof fetch;
}

export async function runDownloads(opts: RunOptions): Promise<DownloadStats> {
  const { root, tasks, signal, onProgress } = opts;
  const doFetch = opts.fetchImpl ?? fetch;
  const concurrency = opts.concurrency ?? DEFAULT_CONCURRENCY;
  const manifest = await loadManifest(root);
  const stats: DownloadStats = { total: tasks.length, success: 0, skipped: 0, noVideo: 0, failed: 0, bytes: 0 };

  const handleTask = async (task: DownloadTask): Promise<void> => {
    if (!task.videoURL && !task.resolveURL) {
      stats.noVideo++;
      onProgress({ task, status: 'no-video' });
      return;
    }
    if (isRecorded(manifest, task)) {
      try {
        const { dir, fileName } = await getParentDirAndName(root, task.localPath, false);
        if (await fileExists(dir, fileName)) {
          stats.skipped++;
          onProgress({ task, status: 'skipped' });
          return;
        }
      } catch { /* 父目录不存在 → 当作未下载，继续下 */ }
    }
    try {
      const downloadURL = task.resolveURL ? await task.resolveURL() : task.videoURL;
      if (!downloadURL) {
        stats.noVideo++;
        onProgress({ task, status: 'no-video' });
        return;
      }
      if (!isAllowedVideoUrl(downloadURL)) {
        stats.failed++;
        onProgress({ task, status: 'failed', error: 'untrusted host' });
        return;
      }
      onProgress({ task, status: 'downloading', bytes: 0 });
      const resp = await doFetch(downloadURL, { signal });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const totalBytes = Number(resp.headers.get('content-length')) || undefined;
      const { dir, fileName } = await getParentDirAndName(root, task.localPath, true);
      const n = await writeStream(dir, fileName, resp.body, (bytes) =>
        onProgress({ task, status: 'downloading', bytes, totalBytes }));
      manifest[manifestKey(task)] = {
        shotId: task.shotId, versionId: task.versionId, videoURL: manifestSource(task),
        localPath: task.localPath, tag: task.tag, downloadedAt: new Date().toISOString(),
      };
      stats.success++;
      stats.bytes += n;
      onProgress({ task, status: 'done', bytes: n, totalBytes });
    } catch (e) {
      if (signal.aborted) {
        onProgress({ task, status: 'failed', error: 'cancelled' });
        return;
      }
      stats.failed++;
      onProgress({ task, status: 'failed', error: e instanceof Error ? e.message : String(e) });
    }
  };

  let cursor = 0;
  const worker = async (): Promise<void> => {
    while (cursor < tasks.length) {
      if (signal.aborted) return;
      const task = tasks[cursor++];
      await handleTask(task);
    }
  };

  const workerCount = Math.min(concurrency, Math.max(1, tasks.length));
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  await saveManifest(root, manifest).catch(() => { /* 保存失败不影响已下文件 */ });
  return stats;
}
